import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from '../../utils/api';
import { useMessages } from '../../components/Messages';
import { parseApiError, getErrorMessage } from '../../utils/errorUtils';
import './QuoteConversion.css';
import { formatAppDate } from '../../utils/dateUtils';

function EnhancedQuoteConversion({ quotation, onClose, onConversionComplete }) {
  const { t } = useTranslation('sales');
  const { t: tCommon } = useTranslation('common');
  const { showSuccess, showError } = useMessages();
  const [conversionStatus, setConversionStatus] = useState(null);
  const [remainingLines, setRemainingLines] = useState([]);
  const [conversionHistory, setConversionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [selectedLines, setSelectedLines] = useState([]);
  const [lineQuantities, setLineQuantities] = useState({});
  const [productionQuantities, setProductionQuantities] = useState({}); // Track production quantities per line
  const [allocatedQuantities, setAllocatedQuantities] = useState({}); // Track allocated stock per product
  // No payment_terms here: the sales order takes the quotation's (else the customer's) terms
  const [orderData, setOrderData] = useState({
    shipping_address: '',
    billing_address: '',
    notes: '',
    priority: 'NORMAL'
  });

  const quoteId = quotation.quotation_id || quotation.id;

  // Calculate available stock after considering allocations from previous conversions
  const getAvailableStock = (line) => {
    const baseStock = line.stock_available || 0;
    const allocated = allocatedQuantities[line.product_id] || 0;
    const available = Math.max(0, baseStock - allocated);

    return available;
  };

  // Calculate the maximum quantity that can be converted for a line (from quote)
  const getMaxConvertibleQuantity = (line) => {
    return (line.quantity || 0) - (line.quantity_converted || 0);
  };

  // Calculate quantities breakdown for a line
  const getQuantityBreakdown = (line, conversionQty) => {
    const availableStock = getAvailableStock(line);
    const fromStock = Math.min(conversionQty, availableStock);
    const needsProduction = Math.max(0, conversionQty - availableStock);

    return {
      fromStock,
      needsProduction,
      totalConversion: conversionQty
    };
  };

  useEffect(() => {
    fetchConversionData();
  }, [quoteId]);

  const fetchConversionData = async () => {
    try {
      setLoading(true);
      
      // Fetch conversion status
      const statusResponse = await fetchWithAuth(`/api/enhanced/quotes/${quoteId}/conversion-status`);
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setConversionStatus(statusData);
        const history = statusData.conversion_history || [];
        setConversionHistory(history);

        // Calculate allocated quantities from conversion history
        // Only count allocations from non-shipped orders (exclude SHIPPED, INVOICED, DELIVERED)
        const allocations = {};
        history.forEach(record => {
          const productId = record.product_id;
          const salesOrderStatus = record.sales_order_status;

          // Only count allocations that are still active (not shipped/invoiced)
          if (productId && salesOrderStatus && !['SHIPPED', 'INVOICED', 'DELIVERED'].includes(salesOrderStatus)) {
            allocations[productId] = (allocations[productId] || 0) + (record.quantity_converted || 0);
          }
        });
        setAllocatedQuantities(allocations);

        // Log for debugging (can be removed later)
        console.log('Calculated allocations:', allocations);

        // Initialize line quantities with available quantities
        const quantities = {};
        statusData.lines.forEach(line => {
          quantities[line.line_id] = line.available_quantity;
        });
        setLineQuantities(quantities);
      }
      
      // Fetch remaining convertible lines
      const linesResponse = await fetchWithAuth(`/api/enhanced/quotes/${quoteId}/remaining-lines`);
      if (linesResponse.ok) {
        const linesData = await linesResponse.json();
        const lines = linesData.convertible_lines || [];
        setRemainingLines(lines);

        // Initialize line quantities with actual convertible amounts (min of remaining quote qty and stock)
        const quantities = {};
        lines.forEach(line => {
          const remainingFromQuote = (line.quantity || 0) - (line.quantity_converted || 0);
          const stockAvailable = line.stock_available || 0;
          quantities[line.line_id] = Math.min(remainingFromQuote, stockAvailable);
        });
        setLineQuantities(quantities);
      }
      
      // Auto-populate addresses if customer data is available
      if (quotation.customer_address) {
        setOrderData(prev => ({
          ...prev,
          shipping_address: quotation.customer_address,
          billing_address: quotation.customer_address
        }));
      }
    } catch (err) {
      showError(t('enhancedQuoteConversion.toasts.fetchFailed'), err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLineSelection = (lineId) => {
    setSelectedLines(prev => {
      if (prev.includes(lineId)) {
        return prev.filter(id => id !== lineId);
      } else {
        return [...prev, lineId];
      }
    });
  };

  const handleQuantityChange = (lineId, value) => {
    const line = remainingLines.find(l => l.line_id === lineId);
    if (!line) return;

    const remainingFromQuote = (line.quantity || 0) - (line.quantity_converted || 0);
    const newQty = Math.min(Math.max(0, parseFloat(value) || 0), remainingFromQuote);

    setLineQuantities(prev => ({
      ...prev,
      [lineId]: newQty
    }));

    // Auto-calculate production quantity when conversion qty exceeds stock
    const availableStock = getAvailableStock(line);
    if (newQty > availableStock) {
      const shortageQty = newQty - availableStock;
      setProductionQuantities(prev => ({
        ...prev,
        [lineId]: Math.max(shortageQty, prev[lineId] || 0)
      }));
    } else {
      // Clear production quantity if no shortage
      setProductionQuantities(prev => {
        const updated = { ...prev };
        delete updated[lineId];
        return updated;
      });
    }
  };

  const handleProductionQuantityChange = (lineId, value) => {
    const newQty = Math.max(0, parseFloat(value) || 0);
    setProductionQuantities(prev => ({
      ...prev,
      [lineId]: newQty
    }));
  };

  const handleSelectAll = () => {
    if (selectedLines.length === remainingLines.length) {
      setSelectedLines([]);
    } else {
      setSelectedLines(remainingLines.map(line => line.line_id));
    }
  };

  const handleConvert = async () => {
    if (selectedLines.length === 0) {
      showError(t('enhancedQuoteConversion.toasts.selectAtLeastOne'));
      return;
    }

    try {
      setConverting(true);
      
      const linesToConvert = selectedLines.map(lineId => {
        const line = remainingLines.find(l => l.line_id === lineId);
        const qtyRequested = lineQuantities[lineId] || 0;
        const availableStock = getAvailableStock(line);
        const hasShortage = qtyRequested > availableStock;

        // Send what the Cant. a producir box shows (the shortage unless the user typed another amount):
        // when one line is typed and the others are not sent, the backend produces 0 for the others (#358)
        const productionQty = hasShortage ? (productionQuantities[lineId] || qtyRequested - availableStock) : 0;

        // Send the original requested quantity - let backend detect insufficient stock
        return {
          quotation_line_id: lineId,
          quantity_to_convert: qtyRequested,
          production_quantity: productionQty > 0 ? productionQty : undefined // Include production quantity if specified
        };
      });

      const response = await fetchWithAuth(`/api/enhanced/quotes/${quoteId}/convert-lines`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lines: linesToConvert,
          customer_id: quotation.customer_id,
          order_data: orderData
        })
      });

      if (response.ok) {
        const result = await response.json();

        // Handle different conversion outcomes
        if (result.status === 'PRODUCTION_ORDERS_CREATED') {
          // Sales order for what was sold plus production orders for what has to be made
          const order = result.sales_order || result.draft_sales_order || {};
          showSuccess(
            t('enhancedQuoteConversion.toasts.productionOrdersCreatedTitle'),
            `${t('enhancedQuoteConversion.toasts.salesOrderWithProduction', { number: order.so_number })}\n\n${t('enhancedQuoteConversion.toasts.productionOrdersCreated')}:\n${result.production_orders_created.map(po => `• ${po.po_number}: ${Number(po.quantity).toLocaleString()} x ${po.product_code}`).join('\n')}`
          );

          // The quotation reloads its status and converted quantities (null: no second message)
          if (onConversionComplete) onConversionComplete(null);
          onClose();
        } else {
          // Normal successful conversion (sales order created)
          const salesOrderNumber = result.sales_order?.so_number || t('common:ui.unknown');
          showSuccess(
            t('enhancedQuoteConversion.toasts.conversionSuccessful'),
            t('enhancedQuoteConversion.toasts.createdSalesOrder', { number: salesOrderNumber, count: result.lines_converted })
          );

          // Update allocated quantities to reflect stock consumption
          const newAllocations = { ...allocatedQuantities };
          selectedLines.forEach(lineId => {
            const line = remainingLines.find(l => l.line_id === lineId);
            if (line && line.product_id) {
              const convertedQty = lineQuantities[lineId] || 0;
              newAllocations[line.product_id] = (newAllocations[line.product_id] || 0) + convertedQty;
            }
          });
          setAllocatedQuantities(newAllocations);

          if (onConversionComplete && result.sales_order) {
            onConversionComplete(result.sales_order);
          }

          // Refresh the conversion data
          await fetchConversionData();

          // Clear selections but keep allocated quantities
          setSelectedLines([]);

          // Close the modal immediately
          onClose();
        }
      } else {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData
          ? getErrorMessage(parseApiError(errorData), tCommon)
          : t('enhancedQuoteConversion.toasts.convertLinesFailed'));
      }
    } catch (err) {
      showError(t('convertQuoteToProduction.toasts.conversionFailed'), err.message);
    } finally {
      setConverting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Check if any selected lines need production (conversion qty exceeds available stock)
  const hasProductionNeeded = selectedLines.some(lineId => {
    const line = remainingLines.find(l => l.line_id === lineId);
    if (!line) return false;
    const availableStock = getAvailableStock(line);
    const qtyToConvert = lineQuantities[lineId] || 0;

    // Need production if conversion quantity exceeds available stock
    return qtyToConvert > availableStock;
  });

  // Determine button text based on whether production is needed
  const getConvertButtonText = () => {
    if (converting) return t('enhancedQuoteConversion.buttons.processing');
    if (selectedLines.length === 0) return t('convertQuoteToProduction.selectLinesToConvert');
    if (hasProductionNeeded) return t('enhancedQuoteConversion.buttons.createProductionOrders');
    return t('quotations.view.buttons.convert');
  };

  const getStatusBadge = (status) => {
    const statusClass = status?.toLowerCase().replace('_', '-');
    return <span className={`status-badge ${statusClass}`}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="quote-conversion-modal">
        <div className="modal-content">
          <div className="loading-spinner">{t('convertQuoteToProduction.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="quote-conversion-modal">
      <div className="modal-content enhanced-conversion">
        <div className="modal-header">
          <h2>{t('enhancedQuoteConversion.title')}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="conversion-summary">
          <div className="summary-card">
            <h3>{t('enhancedQuoteConversion.quoteSummary')}</h3>
            <div className="summary-details">
              <div className="detail-row">
                <span>{t('convertQuoteToProduction.quoteNumber')}:</span>
                <strong>{conversionStatus?.summary?.quotation_number}</strong>
              </div>
              <div className="detail-row">
                <span>{t('common:ui.customer')}:</span>
                <strong>{conversionStatus?.summary?.customer_name}</strong>
              </div>
              <div className="detail-row">
                <span>{t('common:ui.status')}:</span>
                {getStatusBadge(conversionStatus?.summary?.status)}
              </div>
              <div className="detail-row">
                <span>{t('enhancedQuoteConversion.conversionProgress')}:</span>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${conversionStatus?.summary?.conversion_percentage || 0}%` }}
                  />
                  <span className="progress-text">
                    {conversionStatus?.summary?.conversion_percentage?.toFixed(1) || 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="summary-card">
            <h3>{t('enhancedQuoteConversion.lineStatistics')}</h3>
            <div className="summary-details">
              <div className="detail-row">
                <span>{t('enhancedQuoteConversion.totalLines')}:</span>
                <strong>{conversionStatus?.summary?.total_lines || 0}</strong>
              </div>
              <div className="detail-row">
                <span>{t('enhancedQuoteConversion.fullyConverted')}:</span>
                <strong className="text-success">
                  {conversionStatus?.summary?.lines_fully_converted || 0}
                </strong>
              </div>
              <div className="detail-row">
                <span>{t('quotations.view.status.partiallyConverted')}:</span>
                <strong className="text-warning">
                  {conversionStatus?.summary?.lines_partially_converted || 0}
                </strong>
              </div>
              <div className="detail-row">
                <span>{t('common:ui.pending')}:</span>
                <strong className="text-muted">
                  {(conversionStatus?.summary?.total_lines || 0) - 
                   (conversionStatus?.summary?.lines_fully_converted || 0) - 
                   (conversionStatus?.summary?.lines_partially_converted || 0)}
                </strong>
              </div>
            </div>
          </div>
        </div>


        {remainingLines.length > 0 ? (
          <div className="lines-section">
            <div className="section-header">
              <h3>{t('convertQuoteToProduction.selectLinesToConvert')}</h3>
              <button className="btn-link" onClick={handleSelectAll}>
                {selectedLines.length === remainingLines.length ? t('convertQuoteToProduction.deselectAll') : t('common:ui.selectAll')}
              </button>
            </div>
            
            <table className="lines-table">
              <thead>
                <tr>
                  <th width="40">
                    <input 
                      type="checkbox"
                      checked={selectedLines.length === remainingLines.length && remainingLines.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>{t('enhancedQuoteConversion.columns.line')}</th>
                  <th>{t('common:ui.product')}</th>
                  <th>{t('convertQuoteToProduction.columns.originalQty')}</th>
                  <th>{t('convertQuoteToProduction.columns.converted')}</th>
                  <th>{t('enhancedQuoteConversion.columns.stockAvailable')}</th>
                  <th>{t('enhancedQuoteConversion.columns.qtyToConvert')}</th>
                  {hasProductionNeeded && <th>{t('enhancedQuoteConversion.columns.qtyToProduce')}</th>}
                  <th>{t('common:ui.unitPrice')}</th>
                  <th>{t('enhancedQuoteConversion.columns.lineTotal')}</th>
                  <th>{t('common:ui.status')}</th>
                </tr>
              </thead>
              <tbody>
                {remainingLines.map(line => {
                  const qtyToConvert = lineQuantities[line.line_id] || 0;
                  const lineTotal = qtyToConvert * (line.unit_price || 0);
                  
                  return (
                    <tr key={line.line_id}>
                      <td>
                        <input 
                          type="checkbox"
                          checked={selectedLines.includes(line.line_id)}
                          onChange={() => handleLineSelection(line.line_id)}
                        />
                      </td>
                      <td>{line.line_number}</td>
                      <td>
                        <div>
                          <strong>{line.product_code}</strong>
                          <div className="text-muted">{line.product_name}</div>
                        </div>
                      </td>
                      <td className="text-right">{line.quantity}</td>
                      <td className="text-right">{line.quantity_converted || 0}</td>
                      <td className="text-right">
                        <div className="stock-info">
                          {(() => {
                            const availableStock = getAvailableStock(line);
                            const remainingFromQuote = (line.quantity || 0) - (line.quantity_converted || 0);
                            const shortage = remainingFromQuote - availableStock;

                            return (
                              <>
                                <strong className={availableStock < remainingFromQuote ? 'text-warning' : 'text-success'}>
                                  {availableStock}
                                </strong>
                                {(allocatedQuantities[line.product_id] || 0) > 0 && (
                                  <div className="allocated-info text-info">
                                    <small>{t('enhancedQuoteConversion.allocated', { qty: allocatedQuantities[line.product_id] })}</small>
                                  </div>
                                )}
                                {shortage > 0 && (
                                  <div className="stock-shortage text-danger">
                                    <small>{t('enhancedQuoteConversion.need', { qty: shortage })}</small>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="qty-input"
                          value={qtyToConvert}
                          onChange={(e) => handleQuantityChange(line.line_id, e.target.value)}
                          min="0"
                          max={getMaxConvertibleQuantity(line)}
                          step="1"
                        />
                      </td>
                      {hasProductionNeeded && (
                        <td>
                          {(() => {
                            const qtyToConvert = lineQuantities[line.line_id] || 0;
                            const availableStock = getAvailableStock(line);
                            const needsProduction = qtyToConvert > availableStock;

                            if (needsProduction) {
                              const shortageQty = qtyToConvert - availableStock;
                              const productionQty = productionQuantities[line.line_id] || shortageQty;

                              return (
                                <input
                                  type="number"
                                  className="qty-input"
                                  value={productionQty}
                                  onChange={(e) => handleProductionQuantityChange(line.line_id, e.target.value)}
                                  min={shortageQty}
                                  step="1"
                                  placeholder={t('enhancedQuoteConversion.minPlaceholder', { qty: shortageQty })}
                                  title={t('enhancedQuoteConversion.minProductionTitle', { qty: shortageQty })}
                                />
                              );
                            }
                            return <span className="text-muted">-</span>;
                          })()}
                        </td>
                      )}
                      <td className="text-right">{formatCurrency(line.unit_price)}</td>
                      <td className="text-right">
                        <strong>{formatCurrency(lineTotal)}</strong>
                      </td>
                      <td>
                        <div className="status-column">
                          {getStatusBadge(line.conversion_status)}
                          {(() => {
                            const availableStock = getAvailableStock(line);
                            const remainingFromQuote = (line.quantity || 0) - (line.quantity_converted || 0);
                            if (remainingFromQuote > availableStock && availableStock >= 0) {
                              return (
                                <div className="production-needed text-warning">
                                  <small>⚠ {t('enhancedQuoteConversion.needsProduction')}</small>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={hasProductionNeeded ? "9" : "8"} className="text-right"><strong>{t('common:ui.total')}:</strong></td>
                  <td className="text-right">
                    <strong>
                      {formatCurrency(
                        selectedLines.reduce((sum, lineId) => {
                          const line = remainingLines.find(l => l.line_id === lineId);
                          const qty = lineQuantities[lineId] || 0;
                          return sum + (qty * (line?.unit_price || 0));
                        }, 0)
                      )}
                    </strong>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="no-lines-message">
            {conversionStatus?.summary?.lines_fully_converted === conversionStatus?.summary?.total_lines ? (
              <p>✅ {t('enhancedQuoteConversion.noLines.allConverted')}</p>
            ) : (
              <>
                <p>⚠️ {t('enhancedQuoteConversion.noLines.noneAvailable')}</p>
                <p className="text-muted">
                  {t('enhancedQuoteConversion.noLines.mayOccurIf')}
                  <ul style={{ textAlign: 'left', marginTop: '10px' }}>
                    <li>{t('enhancedQuoteConversion.noLines.reasonAllConverted')}</li>
                    <li>{t('enhancedQuoteConversion.noLines.reasonProduction')}</li>
                    <li>{t('enhancedQuoteConversion.noLines.reasonRefresh')}</li>
                  </ul>
                </p>
              </>
            )}
          </div>
        )}

        {conversionHistory.length > 0 && (
          <div className="history-section">
            <h3>{t('convertQuoteToProduction.history.title')}</h3>
            <table className="history-table">
              <thead>
                <tr>
                  <th>{t('common:ui.date')}</th>
                  <th>{t('salesOrders.view.formValues.salesOrder')}</th>
                  <th>{t('enhancedQuoteConversion.columns.line')}</th>
                  <th>{t('common:ui.quantity')}</th>
                  <th>{t('enhancedQuoteConversion.columns.convertedBy')}</th>
                </tr>
              </thead>
              <tbody>
                {conversionHistory.map((record, idx) => (
                  <tr key={idx}>
                    <td>{formatAppDate(record.conversion_date)}</td>
                    <td>
                      <a href={`/sales/orders/${record.sales_order_id}`}>
                        {record.so_number}
                      </a>
                    </td>
                    <td>{record.so_line_number}</td>
                    <td>{record.quantity_converted}</td>
                    <td>{record.converted_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>

        {hasProductionNeeded && selectedLines.length > 0 && (
          <div className="conversion-info">
            <div className="info-message text-warning">
              <strong>⚠️ {t('enhancedQuoteConversion.productionInfo.title')}</strong>
              <p>
                {t('enhancedQuoteConversion.productionInfo.intro')}
              </p>
              <ul>
                <li><strong>{t('enhancedQuoteConversion.buttons.createProductionOrders')}</strong> {t('enhancedQuoteConversion.productionInfo.forSpecifiedQuantities')}</li>
                <li><strong>{t('enhancedQuoteConversion.productionInfo.salesOrderCreated')}</strong> {t('enhancedQuoteConversion.productionInfo.salesOrderCreatedDetail')}</li>
                <li>{t('enhancedQuoteConversion.productionInfo.specifyQuantities')}</li>
              </ul>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button
            className={`btn-primary ${hasProductionNeeded ? 'btn-warning' : ''}`}
            onClick={handleConvert}
            disabled={converting || selectedLines.length === 0}
          >
            {getConvertButtonText()}
          </button>
          <button className="btn-secondary" onClick={onClose}>
            {t('common:ui.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EnhancedQuoteConversion;